/**
 * Search Service
 *
 * Handles global search API calls with caching.
 */

import { api } from './api.js';

// Simple cache for recent searches
const searchCache = new Map();
const CACHE_TTL = 60000; // 1 minute

/**
 * Perform global search across contracts, invoices, vendors, discrepancies
 * @param {string} query - Search query (min 2 characters)
 * @param {Object} options - Search options
 * @param {number} [options.limit=5] - Max results per entity type
 * @param {string} [options.types=null] - Comma-separated entity types to search
 * @returns {Promise<Object>} Search results grouped by entity type
 */
export async function globalSearch(query, options = {}) {
  const { limit = 5, types = null } = options;

  // Build cache key
  const cacheKey = `${query}|${limit}|${types || 'all'}`;

  // Check cache
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }

  // Build query params
  const params = { q: query, limit };
  if (types) {
    params.types = types;
  }

  // Make API call
  const response = await api.get('/v1/search', params);

  // Extract data from response wrapper
  const data = response?.data || response;

  // Cache result
  searchCache.set(cacheKey, { data, timestamp: Date.now() });

  // Cleanup old cache entries (keep max 50)
  if (searchCache.size > 50) {
    const entries = [...searchCache.entries()];
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    searchCache.delete(entries[0][0]);
  }

  return data;
}

/**
 * Clear the search cache
 */
export function clearSearchCache() {
  searchCache.clear();
}

export default {
  globalSearch,
  clearSearchCache,
};
